/**
 * Which table this page plays (DLY-003).
 *
 *   daily     today's level from the server, exactly as generated and solved
 *   practice  before the first daily table only (owner, 2026-09-25): a random table,
 *             clearly labelled, because the daily game has not started
 *   lab       debug mode, which exists only in local development (D-031): the table
 *             comes from the URL, as the layout lab always did
 *
 * `undefined` means there is nothing honest to play: the page says why instead. A
 * random table is never a stand-in for a daily one that failed to load.
 */
import {
  generateLayout,
  levelToLayout,
  type Layout,
  type Level,
  type TableSizeFt,
} from '@potted/sim';
import type { LabParams } from '../lab.ts';
import type { TodaySource } from './today.ts';

/**
 * `archive`: a past day's table, from the calendar (DLY-005). Played in full, never
 * saved, never counted towards streaks or statistics (D-008).
 */
export type TableMode = 'daily' | 'practice' | 'lab' | 'archive';

export interface GameTable {
  readonly mode: TableMode;
  readonly sizeFt: TableSizeFt;
  readonly layout: Layout;
  readonly ballCount: number;
  /** Par, when there is one: every daily table, a lab link from the report. */
  readonly par: number | undefined;
  /** What the shot log calls this table: the level's id, or the seed it was dealt from. */
  readonly id: string;
  /** Practice only: when the first daily table arrives. */
  readonly firstRolloverMs?: number;
  /** Daily only: when the next table arrives, for the countdown (STA-002). */
  readonly nextRolloverMs?: number;
  /**
   * The server's clock when it answered, and the device's clock when the answer
   * arrived: together they give the server's time for as long as the page is open,
   * however wrong the device's clock is (stats/stats.ts `serverClock`).
   */
  readonly serverNowMs?: number;
  readonly receivedAtMs?: number;
  /** The server's today and the first day with a table: the calendar's range (DLY-005). */
  readonly todayKey?: string;
  readonly firstDateKey?: string;
}

function dealt(lab: LabParams, mode: TableMode, par: number | undefined): GameTable {
  return {
    mode,
    sizeFt: lab.sizeFt,
    layout: generateLayout(lab.seed, { sizeFt: lab.sizeFt, ballCount: lab.ballCount }),
    ballCount: lab.ballCount,
    par,
    id: lab.seed,
  };
}

/**
 * The table to play, given the lab parameters and (outside debug mode) what the server
 * said, and the device's clock when it said it. In debug mode the server is not asked,
 * so `source` is undefined.
 */
export function chooseTable(
  lab: LabParams,
  source: TodaySource | undefined,
  receivedAtMs: number,
): GameTable | undefined {
  if (lab.debug) return dealt(lab, 'lab', lab.par);
  if (source === undefined) return undefined;

  switch (source.kind) {
    case 'daily': {
      const { level } = source;
      return {
        mode: 'daily',
        sizeFt: level.table.sizeFt,
        layout: levelToLayout(level),
        ballCount: level.balls.length,
        par: level.par,
        id: level.id,
        nextRolloverMs: source.nextRolloverMs,
        serverNowMs: source.serverNowMs,
        receivedAtMs,
        todayKey: level.dateKey,
        ...(source.firstDateKey === undefined ? {} : { firstDateKey: source.firstDateKey }),
      };
    }
    case 'not-started':
      // No par: a practice table was never solved, and a guessed par would be a lie.
      return {
        ...dealt(lab, 'practice', undefined),
        firstRolloverMs: source.firstRolloverMs,
        serverNowMs: source.serverNowMs,
        receivedAtMs,
      };
    case 'out-of-date':
    case 'unavailable':
      return undefined;
  }
}

/**
 * A past day from the calendar, played as the archive (DLY-005). It keeps today's
 * clock and calendar range from the daily table, so the countdown and the calendar
 * still describe today.
 */
export function archiveTable(level: Level, today: GameTable): GameTable {
  if (today.mode !== 'daily') throw new Error('archiveTable: the archive opens from a daily table');
  return {
    ...today,
    mode: 'archive',
    sizeFt: level.table.sizeFt,
    layout: levelToLayout(level),
    ballCount: level.balls.length,
    par: level.par,
    id: level.id,
  };
}
